import React, { useMemo, useState } from 'react';
import './Wizard.css';

// Produit sans photo : photo de pizza générique (l'ancienne image était un wrap Biscornue)
const FALLBACK = '/img/margherita.webp';
const money = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`;
const imgUrl = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/b_white,f_jpg/');
};

// IDs that are "exclusive none" options — selecting one clears others, and having non-none items blocks them
// seulement_fromagerie = tacos only: exclusive with all other sauces
const NONE_IDS = ['sans_sauce', 'sans_crudites', 'avec_crudites', 'avec_crudites_e', 'sans_supplements', 'seulement_fromagerie',
  'sans_legumes', 'sans_viande', 'sans_fromage', 'sans_charcuterie'];
// only the frites sauce has dynamic pricing (1st free, 2nd +€0.50)
const SAUCE_GROUP_IDS = ['sauce_frites'];
// formule IDs that trigger the sauce_frites step
const FRITES_FORMULE_IDS = ['frites', 'menu'];
// tacos sauce groups: sans_sauce auto-pairs with sans_fromagere (fromagerie is a base sauce)
const TACOS_SAUCE_IDS = ['sauce_tacos'];

function normalizeGroup(g) {
  const items = (g.options || g.items || []).map(it => ({
    ...it,
    label: it.label || it.nom || '',
    price: Number(it.price ?? it.prix ?? 0),
    total: it.total != null ? Number(it.total) : null,
    image: it.image || '',
  })).filter(it => it.id && it.label && !it.hidden);
  return {
    ...g,
    title: g.title || g.titre || 'OPTION',
    subtitle: g.subtitle || `Choisir entre ${g.min || 0} et ${g.max || 1}`,
    note: g.note || '',
    min: Number(g.min || 0),
    max: Number(g.max || 1),
    columns: Number(g.columns || 2),
    items,
  };
}

function Wizard({ produit, optionGroups = [], images = {}, onClose, onAjouter, initialPicks = {}, initialNote = '', initialQty = 1, editMode = false }) {
  const [stepIndex, setStepIndex] = useState(() => {
    if (editMode) {
      // Modification d'un article existant → aller directement au récap
      const ids = Array.isArray(produit.optionGroupIds) ? produit.optionGroupIds : [];
      const byId = optionGroups.reduce((a, g) => ({ ...a, [g.id]: g }), {});
      const stepsLen = ids.filter(id => {
        const g = byId[id]; if (!g) return false;
        const items = (g.options || g.items || []).filter(it => it.id && (it.label || it.nom) && !it.hidden);
        return items.length > 0;
      }).length;
      return stepsLen + 1;
    }
    // Nouvelle commande : si 1er groupe pré-sélectionné (depuis sub-view), passer à étape 1
    const firstGroupId = produit.optionGroupIds?.[0];
    return firstGroupId && initialPicks[firstGroupId]?.length > 0 ? 1 : 0;
  });
  const [picks, setPicks] = useState(initialPicks);
  const [note, setNote] = useState(initialNote);
  const [quantite, setQuantite] = useState(initialQty);

  const steps = useMemo(() => {
    const ids = Array.isArray(produit.optionGroupIds) && produit.optionGroupIds.length > 0
      ? produit.optionGroupIds : [];
    const byId = optionGroups.reduce((acc, g) => ({ ...acc, [g.id]: g }), {});
    return ids
      .map(id => normalizeGroup({ ...(byId[id] || { id }) }))
      .filter(s => s.items.length > 0)
      .map(s => {
        // TOTORO: replace sauce group with single fixed "Sauce spéciale" option
        if (s.id === 'sauce') {
          const bp = picks['type_burger'];
          if (bp && bp[0]?.id === 'totoro') {
            return {
              ...s,
              subtitle: 'Sauce maison incluse',
              note: 'Note importante : la sauce spéciale ne peut pas être modifiée.',
              min: 1, max: 1, columns: 2,
              items: [{ id: 'sauce_speciale', label: 'Sauce spéciale', price: 0, image: '' }],
            };
          }
        }
        return s;
      })
      .filter(s => {
        if (s.id === 'sauce_frites') {
          // Produits sans formule (menu-enfant, petit-faim) → toujours visible
          const hasFormule = (produit.optionGroupIds || []).includes('formule');
          if (!hasFormule) return true;
          const fp = picks['formule'];
          if (!fp || !fp.length) return false;
          return FRITES_FORMULE_IDS.includes(fp[0].id);
        }
        if (s.id === 'viande_tacos_2') {
          const tp = picks['type_tacos'];
          if (!tp || !tp.length) return false;
          return ['t2', 't3'].includes(tp[0].id);
        }
        if (s.id === 'viande_tacos_3') {
          const tp = picks['type_tacos'];
          if (!tp || !tp.length) return false;
          return tp[0].id === 't3';
        }
        // Pour le menu enfant : sauce intérieure seulement si cheeseburger sélectionné
        if (s.id === 'sauce' && produit.id === 'menu-enfant') {
          const cp = picks['choix_enfant'];
          if (!cp || !cp.length) return false;
          return cp[0].id === 'cheese_menu';
        }
        // sauce_frites pour menu enfant : toujours visible (cheese ET nuggets ont des frites)
        if (s.id === 'sauce_frites' && produit.id === 'menu-enfant') return true;
        // crudites_enfant : seulement si cheeseburger sélectionné
        if (s.id === 'crudites_enfant') {
          const cp = picks['choix_enfant'];
          if (!cp || !cp.length) return false;
          return cp[0].id === 'cheese_menu';
        }
        return true;
      });
  }, [produit, optionGroups, picks]);

  const displayName = produit.title || produit.nom || '';
  const basePrice = Number(String(produit.price || produit.visualPrice || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
  const image = produit.image || images[produit.id] || FALLBACK;

  // For summary: show the first selected item image, fallback to product image
  const summaryImage = useMemo(() => {
    for (const s of steps) {
      for (const it of (picks[s.id] || [])) {
        if (it.image) return imgUrl(it.image);
      }
    }
    return image;
  }, [steps, picks, image]);

  const total = useMemo(() => {
    return basePrice + Object.values(picks).flat().reduce((s, it) => s + Number(it.price || 0), 0);
  }, [basePrice, picks]);

  const sel = (id) => picks[id] || [];
  // Pizza à composer : plafond d'ingrédients commun à tous les groupes marqués `ingredient`
  // (les choix « Sans … » ne comptent pas)
  const maxIngredients = Number(produit.maxIngredients) || 0;
  const nbIngredients = steps
    .filter(s => s.ingredient)
    .reduce((n, s) => n + sel(s.id).filter(it => !NONE_IDS.includes(it.id)).length, 0);
  const has = (gid, iid) => sel(gid).some(it => it.id === iid);
  const isNote = stepIndex === steps.length;
  const isSummary = stepIndex >= steps.length + 1;
  const step = steps[stepIndex];

  const isValid = (s = step) => {
    if (!s) return true;
    // sans_fromagere doesn't count toward min/max in tacos sauce group
    const cnt = TACOS_SAUCE_IDS.includes(s.id)
      ? sel(s.id).filter(it => it.id !== 'sans_fromagere').length
      : sel(s.id).length;
    return cnt >= s.min;
  };

  const isDisabled = (s, item) => {
    const cur = sel(s.id);
    const curIds = cur.map(it => it.id);

    const isNone = NONE_IDS.includes(item.id);
    const hasNone = NONE_IDS.some(id => curIds.includes(id));
    const hasNonNone = cur.some(it => !NONE_IDS.includes(it.id));
    // NONE selected → block non-NONE additions (except sans_fromagere already in cur)
    if (hasNone && !isNone && !curIds.includes(item.id)) return true;
    // NONE selected → block other NONE items too (they are mutually exclusive)
    if (hasNone && isNone && !curIds.includes(item.id)) return true;
    // non-NONE selected → block NONE additions
    if (hasNonNone && isNone && !curIds.includes(item.id)) return true;
    if (maxIngredients && s.ingredient && !isNone && !curIds.includes(item.id) && nbIngredients >= maxIngredients) return true;
    // sans_fromagere doesn't consume a slot in tacos sauce group
    const effectiveCount = TACOS_SAUCE_IDS.includes(s.id)
      ? cur.filter(it => it.id !== 'sans_fromagere').length
      : cur.length;
    return s.max > 0 && effectiveCount >= s.max && !curIds.includes(item.id);
  };

  const toggle = (s, item) => {
    // Tacos sauce: sans_sauce auto-pairs with sans_fromagere
    if (TACOS_SAUCE_IDS.includes(s.id)) {
      const cur = sel(s.id);
      const hasSansSauce = cur.some(it => it.id === 'sans_sauce');
      if (item.id === 'sans_sauce') {
        if (hasSansSauce) { setPicks(p => ({ ...p, [s.id]: [] })); return; }
        const fromagereItem = s.items.find(it => it.id === 'sans_fromagere');
        setPicks(p => ({ ...p, [s.id]: fromagereItem ? [item, fromagereItem] : [item] }));
        return;
      }
      // clicking sans_fromagere while sans_sauce is active → deselect both
      if (item.id === 'sans_fromagere' && hasSansSauce) {
        setPicks(p => ({ ...p, [s.id]: [] }));
        return;
      }
    }

    if (s.max === 1) {
      const exists = sel(s.id).some(it => it.id === item.id);
      setPicks(p => ({ ...p, [s.id]: exists ? [] : [item] }));
      return;
    }
    if (isDisabled(s, item) && !has(s.id, item.id)) return;
    const cur = sel(s.id);
    const exists = cur.some(it => it.id === item.id);

    if (exists) {
      if (SAUCE_GROUP_IDS.includes(s.id)) {
        const newList = cur.filter(it => it.id !== item.id);
        let c = 0;
        const repriced = newList.map(it =>
          it.id === 'sans_sauce' ? it : { ...it, price: c++ === 0 ? 0 : 0.50 }
        );
        setPicks(p => ({ ...p, [s.id]: repriced }));
        return;
      }
      setPicks(p => ({ ...p, [s.id]: cur.filter(it => it.id !== item.id) }));
      return;
    }

    const isNone = NONE_IDS.includes(item.id);
    if (isNone) { setPicks(p => ({ ...p, [s.id]: [item] })); return; }

    const cleaned = cur.filter(it => !NONE_IDS.includes(it.id));
    if (SAUCE_GROUP_IDS.includes(s.id)) {
      const nonSans = cleaned.filter(it => it.id !== 'sans_sauce');
      const pricedItem = { ...item, price: nonSans.length >= 1 ? 0.50 : 0 };
      setPicks(p => ({ ...p, [s.id]: [...cleaned, pricedItem].slice(0, s.max) }));
      return;
    }
    // sans_fromagere doesn't consume a slot → allow one extra item in tacos sauce group
    const sliceMax = TACOS_SAUCE_IDS.includes(s.id) && cleaned.some(it => it.id === 'sans_fromagere')
      ? s.max + 1
      : s.max;
    setPicks(p => ({ ...p, [s.id]: [...cleaned, item].slice(0, sliceMax) }));
  };

  // Returns the price label to show on an unselected sauce option
  const getSauceDisplayPrice = (groupId, item) => {
    if (!SAUCE_GROUP_IDS.includes(groupId) || item.id === 'sans_sauce') return item.price;
    const selectedNonSans = sel(groupId).filter(it => it.id !== 'sans_sauce');
    if (selectedNonSans.length >= 1 && !has(groupId, item.id)) return 0.50;
    return item.price;
  };

  // ── Quantity helpers for sauce_frites ──
  const getQty = (groupId, itemId) => sel(groupId).filter(it => it.id === itemId).length;
  const totalQtyInGroup = (groupId) => sel(groupId).filter(it => it.id !== 'sans_sauce').length;

  const increment = (s, item) => {
    const cur = sel(s.id).filter(it => it.id !== 'sans_sauce');
    if (cur.length >= s.max) return;
    const price = cur.length === 0 ? 0 : 0.50;
    setPicks(p => ({ ...p, [s.id]: [...cur, { ...item, price }] }));
  };

  const decrement = (s, item) => {
    const cur = sel(s.id);
    const idx = [...cur].reverse().findIndex(it => it.id === item.id);
    if (idx === -1) return;
    const realIdx = cur.length - 1 - idx;
    const newList = cur.filter((_, i) => i !== realIdx);
    let c = 0;
    const repriced = newList.map(it => ({ ...it, price: c++ === 0 ? 0 : 0.50 }));
    setPicks(p => ({ ...p, [s.id]: repriced }));
  };

  const addToCart = () => {
    const resume = steps.map(s => {
      const items = sel(s.id);
      if (SAUCE_GROUP_IDS.includes(s.id)) {
        const counts = {};
        items.forEach(it => { counts[it.id] = (counts[it.id] || 0) + 1; });
        return Object.entries(counts).map(([id, qty]) => {
          const label = items.find(it => it.id === id)?.label || id;
          return qty > 1 ? `${label} x${qty}` : label;
        }).join(', ');
      }
      return items.map(it => it.label).join(', ');
    }).filter(Boolean).join(' • ');
    const groupOrder = steps.map(s => s.id); // ordre wizard → tri ticket/email
    onAjouter({ label: displayName, resume, note: note.trim(), prixTotal: total * quantite, picks, groupOrder, quantite, produitId: produit.id });
  };

  if (steps.length === 0) {
    return (
      <div className="wiz-overlay" onClick={onClose}>
        <div className="wiz-modal" onClick={e => e.stopPropagation()}>
          <button className="wiz-close" onClick={onClose}>×</button>
          {/* Produit sans option : photo, remarque, quantité */}
          <div className="wiz-simple">
            <img src={image} alt={displayName} className="wiz-simple-img" />
            <h2>{displayName}</h2>
            {produit.description && <p className="wiz-simple-desc">{produit.description}</p>}

            <label className="wiz-simple-note">
              <span>Une remarque&nbsp;?</span>
              <textarea
                className="wiz-note-input"
                maxLength={300}
                placeholder="Allergie, ingrédient à retirer, cuisson…"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </label>

            <div className="wiz-summary-qty">
              <button className="wiz-qty-btn" onClick={() => setQuantite(q => Math.max(1, q - 1))}>−</button>
              <span className="wiz-qty-num">{quantite}</span>
              <button className="wiz-qty-btn" onClick={() => setQuantite(q => q + 1)}>+</button>
            </div>

            <button
              className="wiz-cart-btn"
              onClick={() => onAjouter({
                label: displayName,
                resume: '',
                note: note.trim(),
                prixTotal: basePrice * quantite,
                picks: {},
                quantite,
                produitId: produit.id,
              })}
            >
              Ajouter au panier — {money(basePrice * quantite)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wiz-overlay" onClick={onClose}>
      <div className={`wiz-modal ${isSummary ? 'wiz-modal-summary' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="wiz-close" onClick={onClose}>×</button>

        {!isNote && !isSummary && (
          <>
            <div className="wiz-header">
              <img src={image} alt={displayName} />
              <div className="wiz-header-info">
                <h2>{displayName}</h2>
                <div className="wiz-price">
                  {money(total)}
                  {total > basePrice && <span className="wiz-price-extra">+{money(total - basePrice)}</span>}
                </div>
              </div>
            </div>

            <div className="wiz-progress-row">
              <div className="wiz-progress-bar">
                <div className="wiz-progress-fill" style={{ width: `${((stepIndex + 1) / (steps.length + 1)) * 100}%` }} />
              </div>
              <span className="wiz-step-label">ÉTAPE {stepIndex + 1} SUR {steps.length + 1}</span>
            </div>

            <div className="wiz-step">
              <h3>{step.title}</h3>
              <p className="wiz-step-sub">{step.subtitle}</p>
              {maxIngredients > 0 && step.ingredient && (
                <p className="wiz-step-sub" style={{ fontWeight: 700, color: nbIngredients >= maxIngredients ? 'var(--green)' : undefined }}>
                  {nbIngredients} / {maxIngredients} ingrédients{nbIngredients >= maxIngredients ? ' — maximum atteint' : ''}
                </p>
              )}
              {step.note && (
                <div style={{
                  background: '#FFFBEB',
                  border: '1.5px solid #F59E0B',
                  borderRadius: 8,
                  padding: '10px 14px',
                  marginBottom: 12,
                  fontSize: 13,
                  color: '#92400E',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}>
                  <span>⚠️</span>
                  <span>{step.note}</span>
                </div>
              )}
              {SAUCE_GROUP_IDS.includes(step.id) ? (
                // ── Grid sauce selector with quantity buttons ──
                <>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8}}>
                    {step.items.map(item => {
                      const isSansSauce = item.id === 'sans_sauce';
                      const picked = has(step.id, item.id);
                      const qty = isSansSauce ? 0 : getQty(step.id, item.id);
                      const hasSansSauce = has(step.id, 'sans_sauce');
                      const total4 = totalQtyInGroup(step.id);
                      const canAdd = !isSansSauce && !hasSansSauce && total4 < step.max;
                      const canRemove = !isSansSauce && qty > 0;
                      const itemPrice = sel(step.id).filter(it => it.id === item.id).reduce((s,it) => s+(it.price||0), 0);
                      const isActive = isSansSauce ? picked : qty > 0;
                      return (
                        <div key={item.id} style={{
                          display:'flex', flexDirection:'column', alignItems:'center',
                          borderRadius:12, border: `1.5px solid ${isActive ? '#2D6A4F' : '#E5E7EB'}`,
                          background: isActive ? '#F0FDF4' : '#fff',
                          padding:'10px 6px 8px', position:'relative', transition:'all .15s',
                          cursor: isSansSauce ? 'pointer' : 'default',
                          opacity: (!isSansSauce && hasSansSauce) ? 0.4 : 1,
                        }} onClick={isSansSauce ? () => {
                          if (picked) setPicks(p => ({ ...p, [step.id]: [] }));
                          else setPicks(p => ({ ...p, [step.id]: [item] }));
                        } : undefined}>
                          {isActive && <span style={{position:'absolute',top:6,right:6,fontSize:11,color:'#2D6A4F',fontWeight:900}}>✓</span>}
                          {item.image
                            ? <img src={imgUrl(item.image)} alt={item.label} style={{width:56,height:56,objectFit:'cover',borderRadius:8,marginBottom:6}} />
                            : <div style={{width:56,height:56,borderRadius:8,background:'#F3F4F6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,marginBottom:6}}>🚫</div>
                          }
                          <strong style={{fontSize:11,textAlign:'center',color:'#111',lineHeight:1.3,marginBottom:4}}>{item.label}</strong>
                          {!isSansSauce && (
                            <>
                              {qty > 0 && (
                                <span style={{fontSize:10,fontWeight:700,color: itemPrice===0 ? '#2D6A4F' : '#6B7280',marginBottom:4}}>
                                  {itemPrice === 0 ? 'Gratuit' : `+${money(itemPrice)}`}
                                </span>
                              )}
                              <div style={{display:'flex',alignItems:'center',gap:4,marginTop:'auto'}}>
                                <button type="button" onClick={() => decrement(step, item)} disabled={!canRemove}
                                  style={{width:26,height:26,borderRadius:'50%',border:'1.5px solid #E5E7EB',
                                    background: canRemove?'#fff':'#F9FAFB', cursor: canRemove?'pointer':'default',
                                    fontSize:16,fontWeight:900,color: canRemove?'#2D6A4F':'#D1D5DB',lineHeight:1,padding:0}}>−</button>
                                <span style={{fontSize:15,fontWeight:900,minWidth:16,textAlign:'center',color:'#111'}}>{qty}</span>
                                <button type="button" onClick={() => increment(step, item)} disabled={!canAdd}
                                  style={{width:26,height:26,borderRadius:'50%',border:'1.5px solid',
                                    borderColor: canAdd?'#2D6A4F':'#E5E7EB',
                                    background: canAdd?'#2D6A4F':'#F9FAFB', cursor: canAdd?'pointer':'default',
                                    fontSize:16,fontWeight:900,color: canAdd?'#fff':'#D1D5DB',lineHeight:1,padding:0}}>+</button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{textAlign:'right', fontSize:12, color:'#9CA3AF', marginTop:4}}>
                    {totalQtyInGroup(step.id)} / max {step.max}
                    {totalQtyInGroup(step.id) > 1 && <span style={{color:'#2D6A4F',fontWeight:700,marginLeft:8}}>+{money(0.50*(totalQtyInGroup(step.id)-1))}</span>}
                  </div>
                </>
              ) : (() => {
                const stepHasImages = step.items.some(it => it.image);
                return (
                  <div className={`wiz-options wiz-cols-${step.columns} ${step.compact ? 'wiz-compact' : ''} ${step.sauce ? 'wiz-sauce' : ''} ${step.menu ? 'wiz-menu' : ''}`}>
                    {step.items.map(item => {
                      const picked = has(step.id, item.id);
                      const disabled = isDisabled(step, item);
                      const displayPrice = item.price;
                      const showPrice = item.total != null || displayPrice > 0;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`wiz-option ${picked ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                          onClick={() => toggle(step, item)}
                        >
                          {picked && <span className="wiz-check">✓</span>}
                          {stepHasImages && (
                            item.image
                              ? <img className="wiz-option-img" src={imgUrl(item.image)} alt={item.label} />
                              : <div className="wiz-option-emoji">🍽</div>
                          )}
                          <strong>{item.label}</strong>
                          {item.description && <small>{item.description}</small>}
                          {showPrice && (
                            <em>{item.total != null ? money(item.total) : `+${money(displayPrice)}`}</em>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {!isValid() && (
              <p className="wiz-hint">Sélectionne une option pour continuer</p>
            )}

            <div className="wiz-nav">
              <button className="wiz-btn-back" onClick={() => setStepIndex(i => Math.max(i - 1, 0))} disabled={stepIndex === 0}>← Retour</button>
              <button className="wiz-btn-next" onClick={() => setStepIndex(i => i + 1)} disabled={!isValid()}>
                {stepIndex === steps.length - 1 ? 'Ajouter une note →' : 'Suivant →'}
              </button>
            </div>
          </>
        )}

        {isNote && (
          <>
            <div className="wiz-header">
              <img src={image} alt={displayName} />
              <div className="wiz-header-info">
                <h2>{displayName}</h2>
                <div className="wiz-price">{money(total)}</div>
              </div>
            </div>

            <div className="wiz-progress-row">
              <div className="wiz-progress-bar">
                <div className="wiz-progress-fill" style={{ width: '100%' }} />
              </div>
              <span className="wiz-step-label">ÉTAPE {steps.length + 1} SUR {steps.length + 1}</span>
            </div>

            <div className="wiz-step wiz-note-step">
              <h3>📝 UNE NOTE POUR LA CUISINE ?</h3>
              <p className="wiz-step-sub">Allergie, ingrédient à retirer, cuisson… écris tout ici.</p>
              <textarea
                className="wiz-note-input"
                placeholder="Ex : sans gluten, bien cuit, allergie aux noix..."
                value={note}
                onChange={e => setNote(e.target.value)}
                maxLength={300}
                rows={4}
              />
              <div className="wiz-note-counter">{note.length}/300</div>
            </div>

            <div className="wiz-nav">
              <button className="wiz-btn-back" onClick={() => setStepIndex(steps.length - 1)}>← Retour</button>
              <button className="wiz-btn-next" onClick={() => setStepIndex(steps.length + 1)}>
                Voir le récapitulatif →
              </button>
            </div>
          </>
        )}

        {isSummary && (
          <div className="wiz-summary">
            <div className="wiz-summary-left">
              <img className="wiz-summary-img" src={summaryImage} alt={displayName} />
              <div className="wiz-summary-title">RÉCAPITULATIF</div>
              <div className="wiz-summary-rows">
                {steps.map(s => {
                  let label;
                  if (SAUCE_GROUP_IDS.includes(s.id)) {
                    const counts = {};
                    sel(s.id).forEach(it => { counts[it.id] = (counts[it.id] || 0) + 1; });
                    label = Object.entries(counts).map(([id, qty]) => {
                      const l = sel(s.id).find(it => it.id === id)?.label || id;
                      return qty > 1 ? `${l} x${qty}` : l;
                    }).join(', ') || '—';
                  } else {
                    label = sel(s.id).map(it => it.label).join(', ') || '—';
                  }
                  return (
                    <div className="wiz-summary-row" key={s.id}>
                      <div className="wiz-summary-dot">✓</div>
                      <div>
                        <strong>{s.title.replace('CHOISIS TON ', '').replace('TON ', '')}</strong>
                        <p>{label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {note.trim() && (
                <div className="wiz-summary-row">
                  <div className="wiz-summary-dot">📝</div>
                  <div>
                    <strong>Note</strong>
                    <p>{note}</p>
                  </div>
                </div>
              )}
              <div className="wiz-summary-qty">
                <button className="wiz-qty-btn" onClick={() => setQuantite(q => Math.max(1, q - 1))}>−</button>
                <span className="wiz-qty-num">{quantite}</span>
                <button className="wiz-qty-btn" onClick={() => setQuantite(q => q + 1)}>+</button>
              </div>
              <div className="wiz-summary-total">
                <span>TOTAL</span>
                <strong>{money(total * quantite)}</strong>
              </div>
            </div>
            <div className="wiz-summary-right">
              <button className="wiz-cart-btn" onClick={addToCart}>
                <span>🛒</span>
                AJOUTER AU PANIER
              </button>
              <button className="wiz-summary-back-btn" onClick={() => setStepIndex(steps.length)}>← Modifier</button>
              <button className="wiz-continue-btn" onClick={onClose}>Continuer mes achats</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Wizard;
